import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, CheckIcon, XMarkIcon,
  GlobeAltIcon, ShieldCheckIcon, LinkIcon, SignalIcon, Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ContextTabLink from './common/ContextTabLink';
import CiSelector from './CiSelector';
import ServiceOverviewMap from './ServiceOverviewMap';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const EMPTY = {
  service_id: '',
  vhost: '',
  root_path: '',
  backend_service_id: '',
  host_server_id: '',
  host_vm_id: '',
  status: 'online',
  tls_cert_ref: '',
  tls_source: 'none',
  internal_ports: '',
  notes: '',
};

export default function WebSiteForm({ site, companyId, meta = {}, services = [], onClose, onSaved, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  /* Variante «tarjetas» (estándar del CMDB); la clásica no cambia. */
  const isCards = variant === 'cards';
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const isEdit = Boolean(site?.id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState(isCards
    ? (site?.id ? 'resumen' : 'sitio')
    : (site?.id && initialMode === 'view' ? 'resumen' : 'sitio'));

  useEffect(() => {
    if (site) {
      setForm({
        service_id: site.service_id || '',
        vhost: site.vhost || '',
        root_path: site.root_path || '',
        backend_service_id: site.backend_service_id || '',
        host_server_id: site.host_server_id || '',
        host_vm_id: site.host_vm_id || '',
        status: site.status || 'online',
        tls_cert_ref: site.tls_cert_ref || '',
        tls_source: site.tls_source || 'none',
        internal_ports: site.internal_ports || '',
        notes: site.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
    setIsDirty(false);
  }, [site, reloadKey]);

  useEffect(() => {
    if (site?.id && initialMode === 'view') setActiveTab('resumen');
  }, [site?.id, initialMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setError('');
  };

  const handleHostServerSelect = (ci) => { setForm(prev => ({ ...prev, host_server_id: ci.ci_id })); setIsDirty(true); };
  const handleHostVmSelect = (ci) => { setForm(prev => ({ ...prev, host_vm_id: ci.ci_id })); setIsDirty(true); };

  /* Descartar: recarga los valores guardados y sale de edición. */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError('');
    if (isEdit) setEditing(false);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.service_id || !payload.vhost.trim()) throw new Error('Servicio y VHost son obligatorios');
      payload.service_id = Number(payload.service_id);
      payload.vhost = payload.vhost.trim();
      payload.backend_service_id = payload.backend_service_id ? Number(payload.backend_service_id) : null;
      payload.host_server_id = payload.host_server_id ? Number(payload.host_server_id) : null;
      payload.host_vm_id = payload.host_vm_id ? Number(payload.host_vm_id) : null;
      if (isEdit) {
        await api.updateWebSite(site.id, payload, companyId);
        // Se guarda sin salir: la ficha se queda abierta (solo se refresca el listado).
        onSaved?.(null);
      } else {
        // Tras crear, mantener el formulario abierto en modo edición para poder
        // seguir añadiendo máquina, TLS y puertos internos sin salir.
        const created = registroGuardado(await api.createWebSite(payload, companyId));
        onSaved?.(created);
      }
      setIsDirty(false);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
      return false;
    } finally { setSaving(false); }
  };

  const serviceLabel = (id) =>
    services.find(s => s.id === Number(id))?.display_name ||
    services.find(s => s.id === Number(id))?.name || id;

  const serviceName = form.service_id ? serviceLabel(form.service_id) : (site?.service_name || 'Sin servicio');
  const backendName = form.backend_service_id ? serviceLabel(form.backend_service_id) : (site?.backend_service_name || '');
  const hostName = form.host_server_id
    ? (String(form.host_server_id) === String(site?.host_server_id) ? (site?.host_server_name || `Servidor #${form.host_server_id}`) : `Servidor #${form.host_server_id}`)
    : (form.host_vm_id
      ? (String(form.host_vm_id) === String(site?.host_vm_id) ? (site?.host_vm_name || `VM #${form.host_vm_id}`) : `VM #${form.host_vm_id}`)
      : '');
  const hostRoute = form.host_server_id
    ? `/infrastructure/servers?destacar=${form.host_server_id}`
    : (form.host_vm_id ? `/virt/vms?destacar=${form.host_vm_id}` : null);
  const statusLabel = (s) => ({ online: 'Online', offline: 'Fuera de línea', maintenance: 'Mantenimiento' }[s] || s || '—');
  const tlsLabel = form.tls_source === 'none'
    ? 'Sin TLS'
    : (form.tls_source === 'letsencrypt' ? 'Let\'s Encrypt' : (form.tls_cert_ref ? 'TLS manual' : 'Sin TLS'));

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Sitios web
        </button>
        <DetailCrumb label={form.vhost || 'Nuevo'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Sitio web"
          disabled={saving}
          onChange={(destino) => {
            if (!editing || window.confirm('Hay una edición abierta. ¿Descartar los cambios sin guardar y pasar al siguiente sitio?')) {
              onNavigate?.(destino);
            }
          }}
        />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon />
              Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => { setEditing(true); setActiveTab('sitio'); }}>
                <CheckIcon />
                Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose} disabled={saving}>
                <XMarkIcon />
                Descartar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* SmartBar (solo en la ficha clásica) */}
      {!isCards && (
      <div className="dv-smartbar">
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('sitio')}>
          <div className="dv-smart-btn-icon"><GlobeAltIcon /></div>
          <div className="dv-smart-btn-value">{form.vhost || '—'}</div>
          <div className="dv-smart-btn-label">VHost</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('config')}>
          <div className="dv-smart-btn-icon"><ShieldCheckIcon /></div>
          <div className="dv-smart-btn-value">{form.tls_source !== 'none' ? '✓' : '✗'}</div>
          <div className="dv-smart-btn-label">TLS</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('sitio')}>
          <div className="dv-smart-btn-icon"><LinkIcon /></div>
          <div className="dv-smart-btn-value">{form.backend_service_id ? '✓' : '—'}</div>
          <div className="dv-smart-btn-label">Backend</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('sitio')}>
          <div className="dv-smart-btn-icon"><SignalIcon /></div>
          <div className="dv-smart-btn-value">{statusLabel(form.status)}</div>
          <div className="dv-smart-btn-label">Estado</div>
        </button>
        {isEdit && form.service_id && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('dependencias')}
            title="Servicio y dependencias del sitio">
            <div className="dv-smart-btn-icon"><Squares2X2Icon /></div>
            <div className="dv-smart-btn-value">🔗</div>
            <div className="dv-smart-btn-label">Dependencias</div>
          </button>
        )}
        {site?.id && (
          <DocSmartButton elementType="web_site" elementId={site.id} elementName={form.vhost} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>

        <FormCardsShell
          isCards={isCards}
          header={isCards ? (
            <FormCardsHead
              icon={<GlobeAltIcon style={{ width: 20, height: 20 }} />}
              title={form.vhost}
              onTitleChange={(v) => { setForm(prev => ({ ...prev, vhost: v })); setIsDirty(true); setError(''); }}
              placeholder="app.empresa.com"
              editing={editing}
              saving={saving}
              onSave={handleSave}
              onCancel={onClose}
              onEdit={() => setEditing(true)}
              crumb={[serviceName, backendName, hostName].filter(Boolean).join(' › ')}
              badges={[
                { text: statusLabel(form.status), color: form.status === 'online' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                { text: tlsLabel },
              ]}
              chips={[
                ...(form.internal_ports ? [{ value: form.internal_ports, label: 'puertos' }] : []),
                ...(form.root_path ? [{ value: form.root_path, label: 'root' }] : []),
              ]}
              extra={site?.id ? (
                <DocSmartButton elementType="web_site" elementId={site.id} elementName={form.vhost} companyId={companyId} variant="chip" />
              ) : null}
            />
          ) : (
            <>
              <input
                className="dv-record-title"
                name="vhost"
                value={form.vhost}
                onChange={handleChange}
                placeholder="app.empresa.com"
                autoFocus
                required
              />
            </>
          )}
          tabs={(
            <div className="dv-tabs-nav">
              {isEdit && (
                <TabButton type="button" className={`dv-tab-btn${activeTab === 'resumen' ? ' active' : ''}`} onClick={() => setActiveTab('resumen')}>Resumen</TabButton>
              )}
              <TabButton type="button" className={`dv-tab-btn${activeTab === 'sitio' ? ' active' : ''}`} onClick={() => setActiveTab('sitio')}>Sitio web</TabButton>
              <TabButton type="button" className={`dv-tab-btn${activeTab === 'config' ? ' active' : ''}`} onClick={() => setActiveTab('config')}>Configuración</TabButton>
              {isEdit && form.service_id && (
                <TabButton type="button" className={`dv-tab-btn${activeTab === 'dependencias' ? ' active' : ''}`} onClick={() => setActiveTab('dependencias')}>Dependencias</TabButton>
              )}
            </div>
          )}
        >
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-form" onSubmit={handleSave}>

          {isCards && activeTab === 'resumen' && isEdit && (
            <div className="dv-tab-pane sf2-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>VHost</span><b style={{ fontSize: '0.95rem' }}>{form.vhost || '—'}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusLabel(form.status)}</b></div>
                <div className="sf2-kpi"><span>TLS</span><b style={{ fontSize: '0.95rem' }}>{form.tls_source !== 'none' ? '✓' : '✗'}</b></div>
                <div className="sf2-kpi"><span>Backend</span><b style={{ fontSize: '0.95rem' }}>{backendName || '—'}</b></div>
                <div className="sf2-kpi"><span>Puertos</span><b style={{ fontSize: '0.95rem' }}>{form.internal_ports || '—'}</b></div>
              </div>
            </div>
          )}

          {activeTab === 'resumen' && isEdit && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-context-grid">
                <div className="dv-context-card">
                  <div className="dv-context-icon">🧩</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Servicio</div>
                    <div className="dv-context-title">{serviceName}</div>
                    <div className="dv-context-sub">{form.vhost || 'Sin vhost'}</div>
                    {form.service_id && (
                      <div className="dv-context-actions">
                        <Link to={`/services?destacar=${form.service_id}&abrir=1`} className="dv-context-link">Abrir servicio ↗</Link>
                        <Link to={`/services/designer?service=${form.service_id}`} className="dv-context-link">Diseñador ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🔗</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Backend</div>
                    <div className="dv-context-title">{backendName || 'Sin backend enlazado'}</div>
                    <div className="dv-context-sub">{form.root_path || 'Sin root path'}</div>
                    {form.backend_service_id && (
                      <div className="dv-context-actions">
                        <Link to={`/services?destacar=${form.backend_service_id}&abrir=1`} className="dv-context-link">Abrir backend ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🖥️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Host y TLS</div>
                    <div className="dv-context-title">{hostName || 'Sin host enlazado'}</div>
                    <div className="dv-context-sub">{tlsLabel}{form.tls_cert_ref ? ` · ${form.tls_cert_ref}` : ''}</div>
                    {hostRoute && (
                      <div className="dv-context-actions">
                        <Link to={hostRoute} className="dv-context-link">Abrir host ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card dv-context-card-warn">
                  <div className="dv-context-icon">⚠️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Impacto</div>
                    <div className="dv-context-title">{form.vhost || 'Sitio sin vhost'}</div>
                    <div className="dv-context-sub">El sitio es la cara pública del servicio: si cae, el servicio y su backend quedan afectados.</div>
                    <div className="dv-context-actions">
                      {form.service_id && <ContextTabLink tab="dependencias" onSelect={setActiveTab}>Ver dependencias ↗</ContextTabLink>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Resumen del sitio</div>
                <div className="dv-summary-grid">
                  <div><span>VHost</span><strong>{form.vhost || '—'}</strong></div>
                  <div><span>Servicio</span><strong>{serviceName}</strong></div>
                  <div><span>Backend</span><strong>{backendName || '—'}</strong></div>
                  <div><span>TLS</span><strong>{tlsLabel}</strong></div>
                  <div><span>Puertos internos</span><strong>{form.internal_ports || '—'}</strong></div>
                  <div><span>Estado</span><strong>{statusLabel(form.status)}</strong></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sitio' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Servicio, estado y ubicación</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Servicio <span className="text-danger">*</span></label>
                    <select name="service_id" value={form.service_id} onChange={handleChange} className="form-select" required>
                      <option value="">Selecciona servicio</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} className="form-select">
                      {(meta.web_statuses || ['online','offline','maintenance']).map(s =>
                        <option key={s} value={s}>{statusLabel(s)}</option>
                      )}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Backend (servicio)</label>
                    <select name="backend_service_id" value={form.backend_service_id} onChange={handleChange} className="form-select">
                      <option value="">(ninguno)</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Servidor físico</label>
                    <CiSelector
                      ciType="rack_element"
                      ciId={form.host_server_id}
                      companyId={companyId}
                      fixedType="rack_element"
                      onSelect={handleHostServerSelect}
                      onClear={() => { setForm(prev => ({ ...prev, host_server_id: '' })); setIsDirty(true); }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Máquina virtual</label>
                    <CiSelector
                      ciType="virt_vm"
                      ciId={form.host_vm_id}
                      companyId={companyId}
                      fixedType="virt_vm"
                      onSelect={handleHostVmSelect}
                      onClear={() => { setForm(prev => ({ ...prev, host_vm_id: '' })); setIsDirty(true); }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Configuración, TLS y notas</div>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Ruta raíz del sitio</label>
                    <input name="root_path" value={form.root_path} onChange={handleChange} className="form-control" placeholder="/var/www/html" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Origen del certificado TLS</label>
                    <select name="tls_source" value={form.tls_source} onChange={handleChange} className="form-select">
                      <option value="none">Sin TLS</option>
                      <option value="letsencrypt">Let's Encrypt (automático)</option>
                      <option value="manual">Manual (certificado importado)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Referencia del certificado TLS</label>
                    <input name="tls_cert_ref" value={form.tls_cert_ref} onChange={handleChange} className="form-control" placeholder="acme:cert-123" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Puertos internos (proxy)</label>
                    <input name="internal_ports" value={form.internal_ports} onChange={handleChange} className="form-control" placeholder="8080, 8443…" />
                    <div className="form-text" style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      Puertos donde escucha el servidor web / proxy interno (pueden ser varios, separados por coma).
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notas</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} className="form-control" rows={3} placeholder="Notas operativas del sitio…" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

          {activeTab === 'dependencias' && isEdit && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              <div className="dv-section">
                <div className="dv-section-title">Servicio y dependencias</div>
                <p className="dv-context-help">
                  Este mapa es el del servicio al que pertenece el sitio: de dónde depende y a qué afecta.
                </p>
                {form.service_id ? (
                  <>
                    <div className="dv-context-actions" style={{ marginBottom: 10 }}>
                      <Link to={`/services/designer?service=${form.service_id}`} className="dv-btn dv-btn-secondary">
                        Abrir diseñador ↗
                      </Link>
                    </div>
                    <ServiceOverviewMap
                      serviceId={Number(form.service_id)}
                      companyId={companyId}
                      onOpenService={(id) => navigate(`/services?destacar=${id}&abrir=1`)}
                    />
                  </>
                ) : (
                  <div className="dv-empty-state">Asocia el sitio a un servicio para ver sus dependencias.</div>
                )}
              </div>
            </div>
          )}
        </fieldset>
        <FormDirtyDock
          show={isCards && isDirty}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => handleSave()}
        />
        </FormCardsShell>
        </fieldset>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="web_site"
          ciId={site?.id}
          tipoLegible="el sitio web"
        />
      </div>
    </div>
  );
}
